import React from 'react';
import { Box, Typography, Paper, Container, Divider, Link as MuiLink } from '@mui/material';

const Section = ({ title, children }) => (
    <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a237e', mb: 1.5 }}>{title}</Typography>
        {children}
    </Box>
);

const P = ({ children }) => (
    <Typography variant="body1" sx={{ color: 'text.secondary', mb: 1.5, lineHeight: 1.8 }}>{children}</Typography>
);

const PrivacyPolicy = () => {
    const effectiveDate = 'March 27, 2026';
    const companyName = 'Ohana Therapies LLC';
    const appName = 'Ohana Financial Services';
    const contactEmail = 'jeni.desai@ohanatherapies.com';
    const website = 'https://www.ohanabehavioralservice.com';

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: '#f8f9fa', py: 6 }}>
            <Container maxWidth="md">
                <Paper elevation={0} sx={{ borderRadius: 4, p: { xs: 3, md: 6 }, boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: '#1a237e', mb: 1 }}>
                        Privacy Policy
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        <strong>Effective Date:</strong> {effectiveDate}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                        <strong>Last Updated:</strong> {effectiveDate}
                    </Typography>
                    <Divider sx={{ mb: 4 }} />

                    <P>
                        {companyName} d/b/a {appName} ("we," "our," or "us") operates a private, internal-use
                        financial management application at {website} (the "Service"). This application is used
                        exclusively by the business owner for managing business finances, tax preparation,
                        payroll records, and personal household expense tracking. It is not offered to the
                        public and has no external users. This Privacy Policy describes how data is handled
                        within the Service.
                    </P>

                    <Section title="1. Scope and Access">
                        <P>This application is a private tool used solely by the owner of {companyName}.
                            There is no public registration, no external user accounts, and no consumer-facing
                            functionality. Access is restricted to the business owner via authenticated login
                            with multi-factor authentication.</P>
                    </Section>

                    <Section title="2. Information Collected">
                        <P><strong>Authentication Data:</strong> Sign-in is handled via Google OAuth through
                            Firebase Authentication. This provides a name, email address, and profile photo.
                            No Google password is stored. A phone number is collected for SMS-based
                            multi-factor authentication.</P>
                        <P><strong>Business Financial Data:</strong> Business bank account transactions,
                            payroll journal records, and tax summary calculations are stored for bookkeeping
                            and tax filing purposes.</P>
                        <P><strong>Personal Financial Data:</strong> Personal and household bank or credit
                            card transactions may be connected via Plaid or uploaded via statement files
                            (PDF/CSV). These are stored in separate, isolated database tables from business
                            data. Individual transactions can be classified as "Business" or "Personal" to
                            identify deductible expenses.</P>
                    </Section>

                    <Section title="3. How Data Is Used">
                        <P>All data is used exclusively for the business owner's own purposes:</P>
                        <Typography component="ul" sx={{ color: 'text.secondary', lineHeight: 2, pl: 2 }}>
                            <li>Tracking business income and expenses</li>
                            <li>Preparing annual tax filings and estimated tax payments</li>
                            <li>Storing and reviewing payroll records</li>
                            <li>Identifying deductible business expenses from personal accounts</li>
                            <li>Generating financial reports for the business's accountant (CA)</li>
                        </Typography>
                    </Section>

                    <Section title="4. Third-Party Services">
                        <P><strong>Plaid:</strong> Plaid Inc. is used to securely connect bank and credit
                            card accounts for transaction retrieval. Plaid's privacy policy is available at{' '}
                            <MuiLink href="https://plaid.com/legal/#end-user-privacy-policy" target="_blank" rel="noopener">
                                https://plaid.com/legal
                            </MuiLink>. Only the business owner's own accounts are connected. No third-party
                            consumer data is collected or processed through Plaid.</P>
                        <P><strong>Google / Firebase:</strong> Firebase provides authentication and
                            multi-factor authentication services. Google's privacy policy:{' '}
                            <MuiLink href="https://policies.google.com/privacy" target="_blank" rel="noopener">
                                https://policies.google.com/privacy
                            </MuiLink>.</P>
                        <P><strong>Amazon Web Services (AWS):</strong> The backend and database are hosted on
                            AWS RDS in the United States. AWS infrastructure is SOC 2 and ISO 27001 compliant.</P>
                    </Section>

                    <Section title="5. Data Security">
                        <P>The following security measures are in place:</P>
                        <Typography component="ul" sx={{ color: 'text.secondary', lineHeight: 2, pl: 2 }}>
                            <li>All data in transit is encrypted using TLS 1.2 or higher</li>
                            <li>Multi-factor authentication (SMS) is required for login</li>
                            <li>Database connections are encrypted via AWS RDS</li>
                            <li>Business and personal financial data are stored in separate database tables</li>
                            <li>No public-facing API endpoints expose financial data without authentication</li>
                        </Typography>
                    </Section>

                    <Section title="6. Data Sharing">
                        <P>Financial data is never sold, rented, shared with, or disclosed to any third party.
                            Data is only transmitted to the third-party services listed above (Plaid, Firebase,
                            AWS) as technically necessary to operate the application. No analytics, advertising,
                            or tracking services are used.</P>
                    </Section>

                    <Section title="7. Data Retention">
                        <P>Financial records are retained for tax and bookkeeping purposes in accordance
                            with IRS guidelines (up to 7 years). The business owner may delete any data at
                            any time. Plaid account connections can be disconnected at any time, which stops
                            further data retrieval.</P>
                    </Section>

                    <Section title="8. Changes to This Policy">
                        <P>This policy may be updated if the application's scope changes. Any updates will
                            be reflected on this page with a new effective date.</P>
                    </Section>

                    <Section title="9. Contact">
                        <P>
                            {companyName}<br />
                            Email: <MuiLink href={`mailto:${contactEmail}`}>{contactEmail}</MuiLink><br />
                            Website: <MuiLink href={website} target="_blank" rel="noopener">{website}</MuiLink>
                        </P>
                    </Section>
                </Paper>
            </Container>
        </Box>
    );
};

export default PrivacyPolicy;
